import React, { useCallback, useMemo, useState } from 'react'
import { flow, omit } from 'lodash'
import { useLocation } from 'react-router-dom'
import queryString from 'query-string'
import { inject, observer } from 'mobx-react'
import { Button, CircularProgress, Container, Typography } from '@material-ui/core'
import { createStyles, Theme, makeStyles } from '@material-ui/core/styles'

import UnsubscribeApi from '../../api/UnsubcribeApi'
import { history } from '../browserHistory'
import withTranslation, { WithTranslation } from '../hocs/withTranslation'
import { ReactTitle } from '../utils/MetaTags'
import palette from '../../themes/palette'
import { IStore, IUser } from '../../store'

const OOH_SITE_URL = 'https://www.outofhandtheater.com'
const SUPPORT_EMAIL = 'contact@outofhandtheater.com'

type Status = 'confirm' | 'working' | 'success' | 'error'

interface Props {
  loaded: boolean
  user?: IUser
}

/**
 * The page an emailed "unsubscribe" link lands on.
 *
 * 🔴 THE POST MUST STAY BEHIND A CLICK. Do not move it into a mount effect,
 * however convenient that looks.
 *
 * Until 2026-08 this component fired `post('/unsubscribe')` from a `useEffect`
 * on mount. That was survivable only because it ALSO refused to act without a
 * signed-in session -- a second bug that happened to serve as the gate. Both
 * bugs are gone now, and the on-mount POST cannot come back with them:
 * corporate mail security products (Microsoft Defender for Office 365,
 * Proofpoint URL Defense, Barracuda) fetch every URL in a message to scan it.
 * An unsubscribe that acts on page load fires on each of those prefetches, and
 * the recipient is silently unsubscribed by their own employer's mail filter
 * without ever seeing the page. `src/test/components/unsubscribe/Show.test.tsx`
 * asserts that nothing is requested until the button is pressed.
 *
 * The address comes from `md_email` on the link -- NOT from the session. The
 * old controller acted on `current_user`, so following a colleague's link while
 * signed in unsubscribed you and left them subscribed. The signed-in address is
 * only a fallback for a visitor who somehow arrives without the parameter.
 */
const UnsubscribePage = ({ t, user, loaded }: Props & WithTranslation) => {
  const classes = useStyles()
  const location = useLocation()
  const [status, setStatus] = useState<Status>('confirm')

  const params = useMemo(() => queryString.parse(location.search), [location.search])

  // query-string types a repeated param as string[]; an unsubscribe link only
  // ever carries one address, and acting on an arbitrary one of several would
  // be a guess.
  const rawParam = params.md_email
  const linkEmail = typeof rawParam === 'string' ? rawParam.trim() : ''

  // Held in state because the address is stripped from the URL once we act --
  // without this the success screen could not name what it just unsubscribed.
  const [actedEmail, setActedEmail] = useState('')

  const email = actedEmail || linkEmail || user?.email || ''

  const clearLinkParams = useCallback(() => {
    // Same behaviour as before: once acted on, the address stops travelling in
    // the URL bar, browser history, and any referrer this page leaks.
    const nextSearch = queryString.stringify(omit(params, ['md_email', 'md_id']))
    history.replace({ pathname: location.pathname, search: nextSearch })
  }, [params, location.pathname])

  const handleUnsubscribe = useCallback(async () => {
    if (!email) return

    setActedEmail(email)
    setStatus('working')

    const result = await UnsubscribeApi.create(email)
    const ok = Boolean(result && result.response && result.response.ok)

    setStatus(ok ? 'success' : 'error')
    if (ok) clearLinkParams()
  }, [email, clearLinkParams])

  // The signed-in address is only consulted when the link carried none, so wait
  // for auth in that case rather than flashing "we don't know your address" at
  // someone whose session is still resolving.
  if (!linkEmail && !loaded) {
    return (
      <Container maxWidth='sm' className={classes.root}>
        <ReactTitle title={t('unsubscribe.metaTitle')} />
        <CircularProgress />
      </Container>
    )
  }

  const heading = () => {
    if (status === 'success') return t('auth.unsubscribed1')
    if (status === 'error') return t('unsubscribe.errorTitle')
    if (!email) return t('unsubscribe.missingTitle')
    return t('unsubscribe.confirmTitle')
  }

  const body = () => {
    if (status === 'success') return t('auth.unsubscribed2')
    if (status === 'error') return t('unsubscribe.errorBody', { supportEmail: SUPPORT_EMAIL })
    if (!email) return t('unsubscribe.missingBody')
    return t('unsubscribe.confirmBody')
  }

  return (
    <Container maxWidth='sm' className={classes.root}>
      <ReactTitle title={t('unsubscribe.metaTitle')} />

      <Typography variant='overline' className={classes.eyebrow}>
        {t('unsubscribe.eyebrow')}
      </Typography>

      <Typography variant='h4' component='h1' className={classes.title}>
        {heading()}
      </Typography>

      {email && (
        <Typography variant='body1' component='p' className={classes.email}>
          {email}
        </Typography>
      )}

      <Typography variant='body1' className={classes.message}>
        {body()}
      </Typography>

      <div className={classes.actions}>
        {status === 'confirm' && email && (
          <Button variant='contained' color='primary' disableElevation className={classes.primaryAction} onClick={handleUnsubscribe}>
            {t('unsubscribe.confirmAction')}
          </Button>
        )}

        {status === 'working' && (
          <Button variant='contained' color='primary' disableElevation disabled className={classes.primaryAction}>
            <CircularProgress size={18} className={classes.spinner} />
            {t('unsubscribe.working')}
          </Button>
        )}

        {status === 'error' && (
          <Button variant='contained' color='primary' disableElevation className={classes.primaryAction} onClick={handleUnsubscribe}>
            {t('unsubscribe.retry')}
          </Button>
        )}

        <a href={OOH_SITE_URL} className={classes.siteLink} target='_blank' rel='noopener noreferrer'>
          <Button variant='outlined' fullWidth>
            {t('unsubscribe.visitSite')}
          </Button>
        </a>
      </div>

      {status !== 'success' && email && (
        <Typography variant='body2' className={classes.footnote}>
          {t('unsubscribe.reassurance')}
        </Typography>
      )}
    </Container>
  )
}

export default flow(
  withTranslation,
  observer,
  inject<FIXME, FIXME>(
    ({
      mst: {
        auth: { user, loaded }
      }
    }: {
      mst: IStore
    }) => ({ user, loaded })
  )
)(UnsubscribePage)

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      textAlign: 'center',
      padding: '4rem 1.5rem 5rem',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center'
    },
    eyebrow: {
      color: palette.blue500,
      fontWeight: 700,
      letterSpacing: '1px'
    },
    title: {
      color: palette.grey800,
      marginBottom: '0.75rem'
    },
    email: {
      color: palette.grey800,
      fontWeight: 700,
      wordBreak: 'break-all',
      background: palette.grey100,
      border: `1px solid ${palette.grey200}`,
      borderRadius: '4px',
      padding: '0.5rem 0.9rem',
      marginBottom: '1rem',
      maxWidth: '100%'
    },
    message: {
      color: palette.grey600,
      maxWidth: '30rem',
      marginBottom: '2rem'
    },
    actions: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'stretch',
      gap: '0.75rem',
      width: '100%',
      maxWidth: '20rem',
      [theme.breakpoints.up('sm')]: {
        flexDirection: 'row',
        justifyContent: 'center',
        maxWidth: 'none'
      }
    },
    primaryAction: {
      [theme.breakpoints.up('sm')]: {
        minWidth: '14rem'
      }
    },
    spinner: {
      marginRight: '0.5rem',
      color: 'inherit'
    },
    siteLink: {
      textDecoration: 'none',
      display: 'block',
      [theme.breakpoints.up('sm')]: {
        display: 'inline-block'
      }
    },
    footnote: {
      color: palette.grey500,
      maxWidth: '30rem',
      marginTop: '2rem'
    }
  })
)
